import type { TenantBranding } from './types';

/**
 * The name to SHOW for the current tenant — wordmark, mega-menu panel, marketing header, PWA install
 * name, image alt text.
 *
 * ⚠ THIS EXISTS BECAUSE NINE CALL SITES SAID `branding.tenantName ?? 'ZerEdu'` (BRAND-LEAK).
 * Two things were wrong with that, and the second is worse than the first:
 *
 * 1. The fallback was the PLATFORM's name, on a white-label platform. A school's own admins and
 *    students read it in the sidebar of every page.
 * 2. `tenantName` IS NOT ON THE BRANDING WIRE — see the note on `TenantBranding.appTitle`, which
 *    records the same thing as the reason the browser titles were stuck. So the fallback was not a
 *    rare edge case: it was the value EVERY tenant got, including one who had set a name in
 *    Settings → Branding, because that setting is `appTitle` and nothing here read it.
 *
 * Hence the order below. `appTitle` first because it is the field a tenant can actually set and the
 * one `applyBrandingToDocument` already uses for the browser title — so the tab and the wordmark now
 * agree, which they previously could not. `tenantName` second, for the day the wire carries it.
 *
 * ⚠ THE LAST RESORT IS THE HOSTNAME, NOT A NAME WE CHOSE. It is always available, always the
 * tenant's own (a custom domain or their subdomain), and language-neutral — the same decision as the
 * admin service worker's push fallback, so the two cannot drift. It is never empty, which matters
 * because an empty wordmark reads as a broken header rather than as a setting to fill in.
 *
 * ⚠ DO NOT ADD A `?? 'Something'` AT A CALL SITE. That is exactly the shape this replaces: nine
 * independent fallbacks, none of which could be found from any of the others.
 */
export function brandDisplayName(branding: TenantBranding): string {
  return (
    branding.appTitle?.trim() ||
    branding.tenantName?.trim() ||
    // `globalThis.location` rather than `window` so this is safe in a service worker or a test
    // environment without a DOM; '' can only happen where there is no location at all.
    globalThis.location?.hostname ||
    ''
  );
}
